import React, { Suspense, useContext, useEffect, useState } from 'react'

import { YStack } from 'tamagui'
import type { LoadableComponent } from '@loadable/component'
import { useLocation } from 'react-router-dom'
import { useTitle } from 'hoofd'

import { ServerContext } from '@data/ServerContext'
import { useAuthContext } from '@data/useAuthContext'

import { Spinner } from './Spinner'

export const wrapSuspenseComponent = <T extends {} = {}>(
  Comp: LoadableComponent<T> | React.ComponentType<T> | React.ComponentType<any>
) => {
  return (props: T) => {
    const Component = Comp as React.ComponentType<T>
    return (
      <Suspense fallback={<Spinner size='$9' color='$darkBlue9' />}>
        <Component {...props} />
      </Suspense>
    )
  }
}

const SuspenseLoadingPageTitle = ({ title }: { title: string }) => {
  useTitle(title)
  return null
}

const ScreenFallback = () => (
  <YStack flexGrow={1} alignItems='center' justifyContent='center'>
    <Spinner size='$9' color='$darkBlue9' />
  </YStack>
)

let initialLoad = true

export const wrapSuspenseScreen = <T extends {} = {}>(
  Comp: LoadableComponent<T> | React.ComponentType<T> | React.ComponentType<any>
) => {
  return (props: T) => {
    const Component = Comp as React.ComponentType<T>
    const serverContext = useContext(ServerContext)
    const { accessToken } = useAuthContext()
    const location = useLocation()

    const [hasRendered, setHasRendered] = useState(!initialLoad)

    useEffect(() => {
      if (!hasRendered) {
        initialLoad = false
        setHasRendered(true)
      }
    }, [hasRendered])

    if (
      process.env.NODE_ENV !== 'production' ||
      (!hasRendered && initialLoad && (accessToken || location.state?.inModal))
    ) {
      return (
        <Suspense
          fallback={
            <>
              {!serverContext && (
                <SuspenseLoadingPageTitle title='Loading... | Guild' />
              )}
              <ScreenFallback />
            </>
          }
        >
          <Component {...props} />
        </Suspense>
      )
    }

    return <Component {...props} />
  }
}

export const SuspenseWrapperLoadingSpinner = ({
  size = 'small',
}: {
  variant?: string
  size?: 'small' | 'large'
}) => (
  <Spinner
    size={size === 'large' ? '$9' : '$5'}
    color='$darkBlue9'
  />
)

export const wrapSuspenseDialog = <T extends {} = {}>(
  Comp: LoadableComponent<T> | React.ComponentType<T> | React.ComponentType<any>
) => {
  return (props: T) => {
    const Component = Comp as React.ComponentType<T>
    return (
      <Suspense
        fallback={
          <YStack flex={1} alignItems='center' justifyContent='center'>
            <Spinner size='$9' color='$darkBlue9' />
          </YStack>
        }
      >
        <Component {...props} />
      </Suspense>
    )
  }
}
